import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useError } from '../contexts/ErrorContext';
import { fetchFirebaseError } from '../services/reportsService';
import ReportChart from './ReportChart';
import AnalyticsTimezoneNote from './AnalyticsTimezoneNote';
import { sortVersionsDesc } from '../utils/versionSort';
import './FirebaseErrorReport.css';

const today = new Date().toISOString().slice(0, 10);
const defaultStart = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

const MultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allSelected = selected.length === options.length;
  const noneSelected = selected.length === 0;

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const buttonLabel = noneSelected
    ? `${label}: None`
    : allSelected
      ? `${label}: All`
      : `${label}: ${selected.length}/${options.length}`;

  return (
    <div className="fe-multiselect" ref={ref}>
      <button className="fe-multiselect-btn" onClick={() => setOpen(!open)}>
        {buttonLabel}
        <span className={`fe-multiselect-chevron ${open ? 'open' : ''}`}>&#9662;</span>
      </button>
      {open && (
        <div className="fe-multiselect-dropdown">
          <div className="fe-multiselect-actions">
            <button onClick={() => onChange([...options])}>All</button>
            <button onClick={() => onChange([])}>None</button>
          </div>
          <div className="fe-multiselect-options">
            {options.map(opt => (
              <label key={opt} className="fe-multiselect-option">
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={() => toggle(opt)}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const FirebaseErrorReport = () => {
  const { addError } = useError();
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Available filter options (populated from the first, unfiltered fetch)
  const [allIosVersions, setAllIosVersions] = useState([]);
  const [allAndroidVersions, setAllAndroidVersions] = useState([]);
  const [selectedIosVersions, setSelectedIosVersions] = useState([]);
  const [selectedAndroidVersions, setSelectedAndroidVersions] = useState([]);

  // A filter selection is only valid for the date range it was populated
  // from — reusing it after the range changes can silently zero every
  // count if the old versions don't appear in the new range.
  const resetFilters = () => {
    setAllIosVersions([]);
    setAllAndroidVersions([]);
    setSelectedIosVersions([]);
    setSelectedAndroidVersions([]);
  };

  const runQuery = useCallback(async (useCurrentFilters) => {
    if (!startDate || !endDate) return;
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before or equal to end date.');
      return;
    }

    // Both platforms explicitly emptied means "show nothing" — skip the
    // round trip rather than sending a filter that can only return zero rows.
    if (useCurrentFilters && selectedIosVersions.length === 0 && selectedAndroidVersions.length === 0
      && (allIosVersions.length > 0 || allAndroidVersions.length > 0)) {
      setData(prev => prev && ({
        ...prev,
        summary: { total_flagged_events: 0, total_unique_devices: 0, distinct_codes: 0 },
        byCode: [],
        byEvent: [],
        daily: []
      }));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const filters = useCurrentFilters
        ? { iosVersions: selectedIosVersions, androidVersions: selectedAndroidVersions }
        : null;
      const result = await fetchFirebaseError(startDate, endDate, addError, filters);
      setData(result);

      // First (unfiltered) fetch for this range — populate the version
      // pickers and default them to "everything selected".
      if (!useCurrentFilters) {
        const iosVers = sortVersionsDesc(result.availableVersions?.ios || []);
        const androidVers = sortVersionsDesc(result.availableVersions?.android || []);
        setAllIosVersions(iosVers);
        setAllAndroidVersions(androidVers);
        setSelectedIosVersions(iosVers);
        setSelectedAndroidVersions(androidVers);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch Firebase error report.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, addError, selectedIosVersions, selectedAndroidVersions, allIosVersions.length, allAndroidVersions.length]);

  const handleRunReport = () => runQuery(false);
  const handleApplyFilters = () => runQuery(true);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleRunReport();
  };

  const handleDateChange = (setter) => (e) => {
    setter(e.target.value);
    resetFilters();
  };

  // Transform daily rows into one timeseries per error code
  const buildDailySeries = () => {
    if (!data?.daily?.length) return [];

    const byCode = {};
    for (const row of data.daily) {
      const label = `Code ${row.code}`;
      if (!byCode[label]) byCode[label] = [];
      byCode[label].push({ date: row.date, value: row.event_count });
    }

    return Object.entries(byCode).map(([label, points]) => ({ series: label, data: points }));
  };

  const byCodeTableRows = (data?.byCode || []).map(row => ({
    Code: row.code,
    Meaning: row.meaning,
    Occurrences: row.occurrences,
    Devices: row.unique_devices,
    'First seen': row.first_seen,
    'Last seen': row.last_seen
  }));

  const byEventTableRows = (data?.byEvent || []).map(row => ({
    Event: row.event_name,
    Code: row.code,
    Meaning: row.meaning,
    Occurrences: row.occurrences,
    Devices: row.unique_devices,
    'First seen': row.first_seen,
    'Last seen': row.last_seen
  }));

  const hasVersionOptions = allIosVersions.length > 0 || allAndroidVersions.length > 0;

  return (
    <div className="fe-report">
      <div className="fe-header">
        <div className="fe-header-left">
          <div>
            <h1 className="fe-title">Firebase Errors</h1>
            <p className="fe-subtitle">
              GA4 events flagged with a firebase_error parameter — the SDK's own record of data it dropped before export
            </p>
          </div>
        </div>
        <div className="fe-controls">
          <input
            className="fe-date-input"
            type="date"
            value={startDate}
            onChange={handleDateChange(setStartDate)}
            onKeyDown={handleKeyDown}
          />
          <span className="fe-date-sep">to</span>
          <input
            className="fe-date-input"
            type="date"
            value={endDate}
            onChange={handleDateChange(setEndDate)}
            onKeyDown={handleKeyDown}
          />
          <button
            className="fe-fetch-btn"
            onClick={handleRunReport}
            disabled={loading || !startDate || !endDate}
          >
            {loading ? 'Loading...' : 'Run Report'}
          </button>
        </div>
      </div>

      <AnalyticsTimezoneNote timezone={data?.timezone} />

      {/* Version filter bar — shown once the first fetch has discovered options */}
      {hasVersionOptions && (
        <div className="fe-filters">
          <MultiSelect
            label="iOS Version"
            options={allIosVersions}
            selected={selectedIosVersions}
            onChange={setSelectedIosVersions}
          />
          <MultiSelect
            label="Android Version"
            options={allAndroidVersions}
            selected={selectedAndroidVersions}
            onChange={setSelectedAndroidVersions}
          />
          <button className="fe-filter-apply" onClick={handleApplyFilters} disabled={loading}>
            Apply Filters
          </button>
        </div>
      )}

      {error && <div className="fe-error">{error}</div>}

      {loading && (
        <div className="fe-loading">
          <div className="fe-spinner" />
          Querying BigQuery...
        </div>
      )}

      {!loading && !data && !error && (
        <div className="fe-empty">Select a date range and click Run Report.</div>
      )}

      {!loading && data && (
        <div className="fe-content">
          {/* Summary cards */}
          <div className="fe-summary">
            <div className="fe-card">
              <span className="fe-card-value">
                {data.summary.total_flagged_events.toLocaleString()}
              </span>
              <span className="fe-card-label">Flagged Events</span>
            </div>
            <div className="fe-card">
              <span className="fe-card-value">
                {data.summary.total_unique_devices.toLocaleString()}
              </span>
              <span className="fe-card-label">Unique Devices</span>
            </div>
            <div className="fe-card">
              <span className="fe-card-value">
                {data.summary.distinct_codes.toLocaleString()}
              </span>
              <span className="fe-card-label">Distinct Error Codes</span>
            </div>
          </div>

          {/* Daily trend, one series per error code */}
          {data.daily?.length > 0 && (
            <div className="fe-chart-section">
              <h3 className="fe-section-title">Daily Occurrences by Code</h3>
              <div className="fe-chart-container">
                <ReportChart
                  id="firebase-error-daily"
                  name="Daily Occurrences by Code"
                  type="timeseries"
                  data={buildDailySeries()}
                />
              </div>
            </div>
          )}

          {/* By error code */}
          {byCodeTableRows.length > 0 && (
            <div className="fe-table-section">
              <h3 className="fe-section-title">By Error Code</h3>
              <ReportChart id="firebase-error-by-code" type="table" data={byCodeTableRows} />
            </div>
          )}

          {/* By event */}
          {byEventTableRows.length > 0 && (
            <div className="fe-table-section">
              <h3 className="fe-section-title">By Event</h3>
              <ReportChart id="firebase-error-by-event" type="table" data={byEventTableRows} />
            </div>
          )}

          {byCodeTableRows.length === 0 && byEventTableRows.length === 0 && (
            <div className="fe-empty-filtered">No flagged events match the selected versions.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default FirebaseErrorReport;
